import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import "./FlightTables.css";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import FlightLandIcon from "@mui/icons-material/FlightLand";
import OpenInFull from "@mui/icons-material/OpenInFull";
import Close from "@mui/icons-material/Close";
import ShareIcon from "@mui/icons-material/IosShare";
import CountryInfo from "./CountryInfo";
import {
  AIRPORTS,
  REGIONAL_AIRSPACE,
  FLIGHT_TIME_WINDOW,
  FLIGHT_DISPLAY_CONFIG,
} from "../constants";
import { getAirportByHubCode, storeFlightRecords } from "../utils";
import { findAirlineByCode } from "../airlineRegistry";
import useFlights from "../useFlights";

// Convert AeroAPI delay (seconds) to minutes. Negative = early, treat as no delay.
const toDelayMinutes = (delaySec) => {
  if (delaySec == null || delaySec <= 0) return null;
  return Math.round(delaySec / 60);
};

function FlightTables({
  selectedHub,
  onFlightsUpdate,
  searchQuery,
  onClearSearch,
}) {
  const { t, i18n } = useTranslation();
  const [expandedTable, setExpandedTable] = useState(null);
  const [activeTab, setActiveTab] = useState("departures");
  const [countryInfoOpen, setCountryInfoOpen] = useState(false);

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
  }, []);

  const { flightsData, flightsLoading, lastUpdated, isRefreshing } = useFlights(
    selectedHub,
    onFlightsUpdate,
  );

  // Tick every 60s to keep "Arriving in" times fresh without network calls
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  const departureHeader = useMemo(() => {
    if (selectedHub === REGIONAL_AIRSPACE.hubCode) {
      return `${t("flights.liveDepartures")} - ${t("header.westAfricanAirports")}`;
    }
    return `${t("flights.liveDepartures")} - ${selectedHub}`;
  }, [selectedHub, t]);

  const arrivalHeader = useMemo(() => {
    if (selectedHub === REGIONAL_AIRSPACE.hubCode) {
      return `${t("flights.liveArrivals")} - ${t("header.westAfricanAirports")}`;
    }
    return `${t("flights.liveArrivals")} - ${selectedHub}`;
  }, [selectedHub, t]);

  // FIXED: Issue #1 - Corrected delayed status logic
  const getFlightStatus = useCallback(
    (flightStatus, departureDelay, arrivalDelay, liveData) => {
      // Normalize status to lowercase for consistent matching
      // AeroAPI returns various status strings from FlightAware
      const s = flightStatus?.toLowerCase() || "";

      // En Route — check status string or live position data
      if (
        s.includes("en route") ||
        (liveData?.latitude &&
          liveData?.longitude &&
          liveData?.is_ground === false)
      ) {
        return { text: t("flights.enRoute"), color: "blue", key: "En Route" };
      }

      // Landed / Arrived / Taxiing (post-landing)
      if (s.includes("landed") || s.includes("arrived") || s === "taxiing") {
        return { text: t("flights.arrived"), color: "gray", key: "Arrived" };
      }

      // Cancelled
      if (s.includes("cancelled") || s.includes("canceled")) {
        return {
          text: t("flights.cancelled"),
          color: "red",
          key: "Cancelled",
        };
      }

      // Diverted
      if (s.includes("diverted")) {
        return {
          text: t("flights.diverted"),
          color: "red",
          key: "Diverted",
        };
      }

      // Departed / Left Gate / Active
      if (s === "active" || s.includes("left gate") || s.includes("departed")) {
        return {
          text: t("flights.boarding"),
          color: "orange",
          key: "Boarding",
        };
      }

      // Delayed — explicit status string from FlightAware
      if (s.includes("delayed")) {
        return { text: t("flights.delayed"), color: "orange", key: "Delayed" };
      }

      // Scheduled — check for computed delay
      if (s.includes("scheduled")) {
        if (
          (departureDelay != null && departureDelay > 0) ||
          (arrivalDelay != null && arrivalDelay > 0)
        ) {
          return {
            text: t("flights.delayed"),
            color: "orange",
            key: "Delayed",
          };
        }
        return {
          text: t("flights.onTime"),
          color: "green",
          key: "On Time",
        };
      }

      // Unknown / result unknown
      if (s.includes("unknown") || s === "result") {
        return {
          text: t("flights.onTime"),
          color: "green",
          key: "On Time",
        };
      }

      // Default fallback
      return {
        text: t("flights.onTime"),
        color: "green",
        key: "On Time",
      };
    },
    [t],
  );

  // FIXED: Issue #3 - Use local timezone instead of UTC
  const formatFlightTime = useCallback(
    (isoString, timezone) => {
      if (!isoString) return "N/A";
      const date = new Date(isoString);
      const tz = timezone || "UTC";
      const locale = i18n.language === "fr" ? "fr-FR" : "en-US";

      const timeStr = date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: tz,
      });

      const rawDateStr = date.toLocaleDateString(locale, {
        month: "long",
        day: "numeric",
        timeZone: tz,
      });
      const dateStr = rawDateStr.replace(/(^|\s)([a-zà-ÿ])/g, (_, space, c) => space + c.toUpperCase());

      // Get the raw abbreviation from the browser
      const rawAbbr = date
        .toLocaleTimeString("en-US", {
          timeZoneName: "short",
          timeZone: tz,
        })
        .split(" ")
        .pop();

      // Map offset-based names to standard abbreviations (FlightAware style)
      // Browsers often render IANA zones as "GMT+X" — map to proper abbreviations
      const offsetToAbbr = {
        // UTC / West Africa (GMT+0)
        GMT: "GMT",
        "GMT+0": "GMT",
        UTC: "UTC",
        // West Africa Time (GMT+1)
        "GMT+1": "WAT",
        // Central European
        CET: "CET",
        CEST: "CEST",
        "GMT+2": "EET",
        EET: "EET",
        EEST: "EEST",
        // East Africa
        "GMT+3": "EAT",
        EAT: "EAT",
        // Turkey
        TRT: "TRT",
        // Gulf
        "GMT+4": "GST",
        GST: "GST",
        // South Asia
        "GMT+5": "PKT",
        IST: "IST",
        // East Asia
        "GMT+8": "HKT",
        "GMT+9": "JST",
        JST: "JST",
        KST: "KST",
        // Americas
        "GMT-3": "BRT",
        "GMT-4": "EDT",
        EDT: "EDT",
        "GMT-5": "EST",
        EST: "EST",
        "GMT-6": "CST",
        CST: "CST",
        CDT: "CDT",
        "GMT-7": "MST",
        MST: "MST",
        MDT: "MDT",
        "GMT-8": "PST",
        PST: "PST",
        PDT: "PDT",
      };
      // Fallback: map IANA timezone names directly for older Android browsers
      // that may return unhelpful offset strings
      const ianaToAbbr = {
        "Africa/Abidjan": "GMT",
        "Africa/Accra": "GMT",
        "Africa/Conakry": "GMT",
        "Africa/Dakar": "GMT",
        "Africa/Bamako": "GMT",
        "Africa/Freetown": "GMT",
        "Africa/Banjul": "GMT",
        "Africa/Bissau": "GMT",
        "Africa/Nouakchott": "GMT",
        "Africa/Lagos": "WAT",
        "Africa/Douala": "WAT",
        "Africa/Niamey": "WAT",
        "Africa/Porto-Novo": "WAT",
        "Africa/Libreville": "WAT",
        "Africa/Brazzaville": "WAT",
        "Africa/Kinshasa": "WAT",
        "Africa/Lome": "GMT",
        "Africa/Ouagadougou": "GMT",
        "Africa/Casablanca": "WET",
        "Africa/Tunis": "CET",
        "Africa/Algiers": "CET",
        "Africa/Cairo": "EET",
        "Africa/Addis_Ababa": "EAT",
        "Africa/Nairobi": "EAT",
        "Africa/Johannesburg": "SAST",
        "Europe/Paris": "CET",
        "Europe/Brussels": "CET",
        "Europe/Istanbul": "TRT",
        "Europe/Lisbon": "WET",
        "Europe/Madrid": "CET",
        "Europe/Rome": "CET",
        "Europe/Berlin": "CET",
        "Europe/Luxembourg": "CET",
        "Europe/London": "GMT",
        "Asia/Dubai": "GST",
        "Asia/Riyadh": "AST",
        "Asia/Doha": "AST",
        "America/New_York": "EST",
        "America/Sao_Paulo": "BRT",
      };
      const tzAbbr = offsetToAbbr[rawAbbr] || ianaToAbbr[tz] || rawAbbr;

      return `${timeStr} ${tzAbbr} (${dateStr})`;
    },
    [i18n.language],
  );

  const formatDelayTime = useCallback((minutes) => {
    if (!minutes || minutes <= 0) return "";

    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      if (remainingMinutes === 0) {
        return `${hours}h`;
      }
      return `${hours}h${remainingMinutes}min`;
    }

    return `${minutes}min`;
  }, []);

  const selectedAirportCodes = useMemo(() => {
    if (selectedHub === REGIONAL_AIRSPACE.hubCode) {
      return AIRPORTS.map((airport) => airport.iataCode);
    } else {
      const airport = getAirportByHubCode(selectedHub);
      return airport && airport.iataCode ? [airport.iataCode] : [];
    }
  }, [selectedHub]);

  // FIXED: Issue #5 - Extract shared flight processing logic to avoid duplication
  const processFlights = useCallback(
    (flights, type) => {
      if (flightsLoading || !flights.length) return [];

      const now = new Date();
      // FIXED: Issue #4 - Use configurable time window
      const pastHours = FLIGHT_TIME_WINDOW.PAST_HOURS;
      const futureHours = FLIGHT_TIME_WINDOW.FUTURE_HOURS;
      const pastTime = new Date(now.getTime() - pastHours * 60 * 60 * 1000);
      const futureTime = new Date(now.getTime() + futureHours * 60 * 60 * 1000);

      const timeField = type === "departure" ? "departure" : "arrival";
      const relevantCode = type === "departure" ? "departure" : "arrival";

      const filteredFlights = flights.filter((flight) => {
        const code = flight[relevantCode]?.iata;

        // Skip flights with no scheduled time (private jets with incomplete data)
        if (!flight[timeField]?.scheduled) return false;

        // When searching, use the same time window
        if (searchQuery) {
          const scheduledTime = new Date(flight[timeField]?.scheduled);
          return scheduledTime >= pastTime && scheduledTime <= futureTime;
        }

        // When not searching, filter by selected airports
        if (!code || !selectedAirportCodes.includes(code)) {
          return false;
        }

        const scheduledTime = new Date(flight[timeField]?.scheduled);
        return scheduledTime >= pastTime && scheduledTime <= futureTime;
      });

      // Sort by scheduled time
      const sortedFlights = filteredFlights.sort((a, b) => {
        const timeA = new Date(a[timeField]?.scheduled);
        const timeB = new Date(b[timeField]?.scheduled);

        if (timeA >= now && timeB >= now) {
          return timeA - timeB;
        }
        if (timeA < now && timeB < now) {
          return timeB - timeA;
        }
        return timeA >= now ? -1 : 1;
      });

      // Deduplicate codeshare flights — group by same route + scheduled time
      const scoreFlightData = (f) => {
        const hasLive = f.live?.latitude && f.live?.longitude;
        if (hasLive && f.live?.is_ground === false) return 3; // airborne
        if (f.flight_status === "active") return 2;
        if (hasLive) return 1; // on ground with live data
        return 0;
      };

      const codeshareGroups = new Map();
      sortedFlights.forEach((flight) => {
        const depCode = flight.departure?.iata || "";
        const arrCode = flight.arrival?.iata || "";
        const depTime = flight.departure?.scheduled || "";
        const key = `${depCode}-${arrCode}-${depTime}`;

        if (!codeshareGroups.has(key)) {
          codeshareGroups.set(key, { primary: flight, codeshares: [] });
        } else {
          const group = codeshareGroups.get(key);
          if (scoreFlightData(flight) > scoreFlightData(group.primary)) {
            group.codeshares.push(group.primary);
            group.primary = flight;
          } else {
            group.codeshares.push(flight);
          }
        }
      });

      const deduplicatedFlights = Array.from(codeshareGroups.values()).map(
        ({ primary, codeshares }) => {
          // Build codeshare list from duplicate flight entries
          const csFromDuplicates = codeshares.map((cs) => ({
            flight: cs.flight?.iata || "N/A",
            airline: cs.airline?.name || "—",
          }));

          // Also extract codeshare from AeroAPI's flight.codeshared field
          const csFromApi = primary.flight?.codeshared?.flight_iata
            ? [
                {
                  flight: primary.flight.codeshared.flight_iata,
                  airline:
                    findAirlineByCode(
                      primary.flight.codeshared.flight_iata?.slice(0, 2),
                    )?.name || "",
                },
              ]
            : [];

          primary._codeshares = [...csFromDuplicates, ...csFromApi];
          return primary;
        },
      );

      // Apply search filter if needed
      let flightsToMap = deduplicatedFlights;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        flightsToMap = deduplicatedFlights.filter((flight) => {
          const flightNumber = flight.flight?.iata?.toLowerCase() || "";
          const flightIcao = flight.flight?.icao?.toLowerCase() || "";
          const departureCity = flight.departure?.airport?.toLowerCase() || "";
          const arrivalCity = flight.arrival?.airport?.toLowerCase() || "";
          const departureCode = flight.departure?.iata || "";
          const arrivalCode = flight.arrival?.iata || "";

          // Resolve airline name from registry (same as display)
          const resolvedAirline = (flight.airline?.name || "").toLowerCase();

          // Use AeroAPI country fields directly
          const departureCountry = (flight.originCountry || "").toLowerCase();
          const arrivalCountry = (
            flight.destinationCountry || ""
          ).toLowerCase();

          // Also search within codeshare flight numbers and airlines
          const codeshareMatch = (flight._codeshares || []).some(
            (cs) =>
              cs.flight.toLowerCase().includes(query) ||
              cs.airline.toLowerCase().includes(query),
          );

          return (
            flightNumber.includes(query) ||
            flightIcao.includes(query) ||
            departureCity.includes(query) ||
            arrivalCity.includes(query) ||
            departureCode.toLowerCase().includes(query) ||
            arrivalCode.toLowerCase().includes(query) ||
            resolvedAirline.includes(query) ||
            departureCountry.includes(query) ||
            arrivalCountry.includes(query) ||
            codeshareMatch
          );
        });

        // Apply search limit if configured (0 = unlimited)
        if (FLIGHT_DISPLAY_CONFIG.SEARCH_LIMIT > 0) {
          flightsToMap = flightsToMap.slice(
            0,
            FLIGHT_DISPLAY_CONFIG.SEARCH_LIMIT,
          );
        }
      } else {
        // Apply default limit for normal view
        flightsToMap = deduplicatedFlights.slice(
          0,
          FLIGHT_DISPLAY_CONFIG.DEFAULT_LIMIT,
        );
      }

      const mappedFlights = flightsToMap.map((flight) => {
        const originCode = flight.departure?.iata || "";
        const destCode = flight.arrival?.iata || "";

        // Delay in minutes from AeroAPI seconds field
        const departureDelay = toDelayMinutes(flight.departure?.delay);
        const arrivalDelay = toDelayMinutes(flight.arrival?.delay);

        const hasDepartureEstimated = !!flight.departure?.estimated;
        const hasArrivalEstimated = !!flight.arrival?.estimated;
        const status = getFlightStatus(
          flight.flight_status,
          departureDelay,
          arrivalDelay,
          flight.live,
        );

        const originCountry = flight.originCountry || "";
        const destCountry = flight.destinationCountry || "";
        const translatedOriginCountry = originCountry
          ? t(`countries.${originCountry}`, { defaultValue: originCountry })
          : "";
        const translatedDestCountry = destCountry
          ? t(`countries.${destCountry}`, { defaultValue: destCountry })
          : "";

        // FIXED: Issue #7 - Add gate and terminal information
        const departureGate = flight.departure?.gate;
        const departureTerminal = flight.departure?.terminal;
        const arrivalGate = flight.arrival?.gate;
        const arrivalTerminal = flight.arrival?.terminal;

        return {
          flight: flight.flight?.iata || flight.flight?.icao || "N/A",
          origin: translatedOriginCountry
            ? `${flight.departure?.airport || "Unknown"} (${originCode}) - ${translatedOriginCountry}`
            : `${flight.departure?.airport || "Unknown"} (${originCode})`,
          destination: translatedDestCountry
            ? `${flight.arrival?.airport || "Unknown"} (${destCode}) - ${translatedDestCountry}`
            : `${flight.arrival?.airport || "Unknown"} (${destCode})`,
          airline:
            flight.airline?.name ||
            (!flight.airline?.iata ? t("flights.privateCharter") : "—"),
          departureTime: formatFlightTime(
            flight.departure?.actual_runway ||
              flight.departure?.actual ||
              flight.departure?.estimated ||
              flight.departure?.scheduled,
            flight.departure?.timezone,
          ),
          arrivalTime: formatFlightTime(
            flight.arrival?.actual_runway ||
              flight.arrival?.actual ||
              flight.arrival?.estimated ||
              flight.arrival?.scheduled,
            flight.arrival?.timezone,
          ),
          departureDelayed: departureDelay && !hasDepartureEstimated,
          arrivalDelayed: arrivalDelay && !hasArrivalEstimated,
          departureDelayMinutes: departureDelay,
          arrivalDelayMinutes: arrivalDelay,
          departureTimeHighlight:
            hasDepartureEstimated &&
            flight.departure?.estimated !== flight.departure?.scheduled,
          arrivalTimeHighlight:
            hasArrivalEstimated &&
            flight.arrival?.estimated !== flight.arrival?.scheduled,
          departureGate,
          departureTerminal,
          arrivalGate,
          arrivalTerminal,
          status: status.text,
          statusKey: status.key,
          statusColor: status.color,
          codeshares: flight._codeshares || [],
          _rawDepartureScheduled: flight.departure?.scheduled || "",
          _rawArrivalScheduled: flight.arrival?.scheduled || "",
          duration: (() => {
            const dep =
              flight.departure?.actual_runway ||
              flight.departure?.actual ||
              flight.departure?.estimated ||
              flight.departure?.scheduled;
            const arr =
              flight.arrival?.actual_runway ||
              flight.arrival?.actual ||
              flight.arrival?.estimated ||
              flight.arrival?.scheduled;
            if (!dep || !arr) return null;
            const ms = new Date(arr) - new Date(dep);
            if (ms <= 0) return null;
            const h = Math.floor(ms / 3600000);
            const m = Math.round((ms % 3600000) / 60000);
            return m === 0 ? `${h}h` : `${h}h${m.toString().padStart(2, "0")}`;
          })(),
          // For en-route flights: time remaining until arrival
          arrivingIn:
            status.key === "En Route"
              ? (() => {
                  const arrTime =
                    flight.arrival?.estimated || flight.arrival?.scheduled;
                  if (!arrTime) return null;
                  const ms = new Date(arrTime) - new Date();
                  if (ms <= 0) return null;
                  const h = Math.floor(ms / 3600000);
                  const m = Math.round((ms % 3600000) / 60000);
                  if (h === 0) return `~${m}min`;
                  return m === 0 ? `~${h}h` : `~${h}h${m}min`;
                })()
              : null,
        };
      });

      return mappedFlights;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      flightsLoading,
      selectedAirportCodes,
      getFlightStatus,
      formatFlightTime,
      searchQuery,
      t,
      tick, // Intentional: forces recalculation of arrivingIn every 60s
    ],
  );

  const departures = useMemo(
    () => processFlights(flightsData.departures, "departure"),
    [flightsData.departures, processFlights],
  );

  const arrivals = useMemo(
    () => processFlights(flightsData.arrivals, "arrival"),
    [flightsData.arrivals, processFlights],
  );

  // Store enriched flight records to backend after each data refresh.
  // Debounced + deduped: waits for data to settle, skips if payload unchanged.
  const lastStoredHash = useRef("");
  useEffect(() => {
    if (searchQuery || flightsLoading) return;
    if (departures.length === 0 && arrivals.length === 0) return;

    // Build a lightweight hash to detect meaningful changes
    const hash = `${departures.map((f) => `${f.flight}:${f.statusKey}`).join(",")}|${arrivals.map((f) => `${f.flight}:${f.statusKey}`).join(",")}`;
    if (hash === lastStoredHash.current) return;

    const timer = setTimeout(async () => {
      lastStoredHash.current = hash;

      const airportCodes = selectedAirportCodes.filter((code) =>
        AIRPORTS.some((a) => a.iataCode === code),
      );

      // Send POSTs sequentially to avoid concurrent Lambda cold start 500s
      for (const code of airportCodes) {
        const codeDepartures = departures.filter((f) => {
          const match = f.origin?.match(/\(([A-Z]{3})\)/);
          return match && match[1] === code;
        });
        const codeArrivals = arrivals.filter((f) => {
          const match = f.destination?.match(/\(([A-Z]{3})\)/);
          return match && match[1] === code;
        });

        if (codeDepartures.length > 0) {
          await storeFlightRecords(code, "departure", codeDepartures).catch(
            () => {},
          );
        }
        if (codeArrivals.length > 0) {
          await storeFlightRecords(code, "arrival", codeArrivals).catch(
            () => {},
          );
        }
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [departures, arrivals, searchQuery, flightsLoading, selectedAirportCodes]);

  // Render delay info or "Arriving in X" for en-route flights
  const renderStatusDetail = useCallback(
    (flight) => {
      if (flight.arrivingIn) {
        return (
          <div className="delay-info">
            {t("flights.arrivingIn", { time: flight.arrivingIn })}
          </div>
        );
      }
      return (
        <>
          {flight.departureDelayMinutes > 0 && (
            <div className="delay-info">
              ({t("flights.departure")} {t("flights.delayedBy")}{" "}
              {formatDelayTime(flight.departureDelayMinutes)})
            </div>
          )}
          {flight.arrivalDelayMinutes > 0 && (
            <div className="delay-info">
              ({t("flights.arrival")}{" "}
              {i18n.language === "fr"
                ? t("flights.delayedByFeminine")
                : t("flights.delayedBy")}{" "}
              {formatDelayTime(flight.arrivalDelayMinutes)})
            </div>
          )}
        </>
      );
    },
    [t, i18n.language, formatDelayTime],
  );

  const handleShareFlight = useCallback(
    async (flight) => {
      const lines = [
        `✈ ${t("flights.shareFlightNo")}: ${flight.flight}`,
        `${t("flights.shareAirline")}: ${flight.airline}`,
        "",
        `${t("flights.shareDeparts")}: ${flight.origin}`,
        `${t("flights.shareAt")} ${flight.departureTime}`,
        "",
        `${t("flights.shareArrives")}: ${flight.destination}`,
        `${t("flights.shareAt")} ${flight.arrivalTime}`,
      ];
      if (flight.duration)
        lines.push("", `${t("flights.durationLabel")}: ${flight.duration}`);
      lines.push("", t("flights.shareClickMore"));
      const url = `https://renuairhub.com/dashboard?search=${encodeURIComponent(flight.flight)}`;
      const text = lines.join("\n");

      if (navigator.share) {
        try {
          await navigator.share({
            title: `${t("flights.shareFlightNo")} ${flight.flight} - ${flight.airline}`,
            text,
            url,
          });
        } catch (e) {
          // User cancelled share
        }
      } else {
        try {
          await navigator.clipboard.writeText(`${text}\n\n${url}`);
          const btn = document.activeElement;
          if (btn) {
            const original = btn.innerHTML;
            btn.textContent = "✓";
            setTimeout(() => {
              btn.innerHTML = original;
            }, 1500);
          }
        } catch (e) {
          // Clipboard not available
        }
      }
    },
    [t],
  );

  // Shared table renderer — eliminates ~180 lines of duplication
  const renderFlightTable = useCallback(
    ({
      name,
      headerClass,
      icon,
      header,
      flights: tableFlights,
      skelPrefix,
    }) => {
      const isMobile = window.innerWidth <= 480;
      const MOBILE_PREVIEW_COUNT = 3;
      const isPreview =
        isMobile &&
        !expandedTable &&
        tableFlights.length > MOBILE_PREVIEW_COUNT;
      const visibleFlights = isPreview
        ? tableFlights.slice(0, MOBILE_PREVIEW_COUNT)
        : tableFlights;

      return (
        <div
          className={`table-section ${expandedTable === name ? "expanded" : expandedTable ? "hidden" : ""}`}
        >
          <div className="flight-tab-toggle-bar">
            <div className="flight-tab-toggle">
              <button
                className={`flight-tab ${activeTab === "departures" ? "active" : ""}`}
                onClick={() => handleTabChange("departures")}
              >
                {t("flights.departure")}
                {searchQuery && (
                  <span className="flight-tab-count">{departures.length}</span>
                )}
              </button>
              <button
                className={`flight-tab ${activeTab === "arrivals" ? "active" : ""}`}
                onClick={() => handleTabChange("arrivals")}
              >
                {t("flights.arrival")}
                {searchQuery && (
                  <span className="flight-tab-count">{arrivals.length}</span>
                )}
              </button>
            </div>
          </div>
          <div className={`table-header ${headerClass}`}>
            <div className="table-header-left">
              <span className="icon">{icon}</span>
              <h3>{header}</h3>
            </div>
            <div className="table-header-right">
              <div className="flight-tab-toggle flight-tab-toggle-inline">
                <button
                  className={`flight-tab ${activeTab === "departures" ? "active" : ""}`}
                  onClick={() => handleTabChange("departures")}
                >
                  {t("flights.departure")}
                  {searchQuery && (
                    <span className="flight-tab-count">
                      {departures.length}
                    </span>
                  )}
                </button>
                <button
                  className={`flight-tab ${activeTab === "arrivals" ? "active" : ""}`}
                  onClick={() => handleTabChange("arrivals")}
                >
                  {t("flights.arrival")}
                  {searchQuery && (
                    <span className="flight-tab-count">{arrivals.length}</span>
                  )}
                </button>
              </div>
              <button
                className="expand-table-button"
                onClick={() =>
                  setExpandedTable(expandedTable === name ? null : name)
                }
                aria-label={
                  expandedTable === name ? "Close fullscreen" : "Expand table"
                }
              >
                {expandedTable === name ? <Close /> : <OpenInFull />}
              </button>
            </div>
          </div>
          <div className="table-wrapper">
            <table className="flight-table">
              <thead>
                <tr>
                  <th>{t("flights.flight")}</th>
                  <th>{t("flights.airline")}</th>
                  <th>{t("flights.origin")}</th>
                  <th>{t("flights.destination")}</th>
                  <th>{t("flights.duration")}</th>
                  <th>{t("flights.status")}</th>
                </tr>
              </thead>
              <tbody>
                {flightsLoading && tableFlights.length === 0 && (
                  <>
                    {[...Array(5)].map((_, i) => (
                      <tr
                        key={`skel-${skelPrefix}-${i}`}
                        className="skeleton-row"
                      >
                        <td>
                          <div className="skeleton-cell skeleton-short" />
                        </td>
                        <td>
                          <div className="skeleton-cell skeleton-medium" />
                        </td>
                        <td>
                          <div className="skeleton-cell skeleton-long" />
                        </td>
                        <td>
                          <div className="skeleton-cell skeleton-long" />
                        </td>
                        <td>
                          <div className="skeleton-cell skeleton-short" />
                        </td>
                        <td>
                          <div className="skeleton-cell skeleton-short" />
                        </td>
                      </tr>
                    ))}
                  </>
                )}
                {searchQuery && flightsLoading && tableFlights.length === 0 && (
                  <tr>
                    <td colSpan="6" className="searching-flight-indicator">
                      <div className="searching-flight-text">
                        ✈{" "}
                        {t("flights.searchingFlight", {
                          query: searchQuery.toUpperCase(),
                        })}
                      </div>
                    </td>
                  </tr>
                )}
                {visibleFlights.map((flight, index) => (
                  <tr
                    key={`${flight.flight}-${index}`}
                    className={searchQuery ? "highlighted-row" : ""}
                  >
                    <td className="flight-number">
                      {flight.flight}
                      {flight.codeshares.length > 0 && (
                        <div className="codeshare-info">
                          {flight.codeshares.map((cs) => cs.flight).join(", ")}
                        </div>
                      )}
                    </td>
                    <td className="airline-cell">
                      {flight.airline}
                      {flight.codeshares.length > 0 && (
                        <div className="codeshare-info">
                          {flight.codeshares
                            .filter((cs) => cs.airline)
                            .map((cs) => cs.airline)
                            .join(", ")}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="destination">{flight.origin}</div>
                      <div className="flight-subtext">
                        <span
                          className={
                            flight.departureTimeHighlight ? "time-delayed" : ""
                          }
                        >
                          {flight.departureTime}
                        </span>
                        {(flight.departureGate || flight.departureTerminal) && (
                          <span className="gate-info">
                            {flight.departureTerminal &&
                              ` • ${t("flights.terminal")} ${flight.departureTerminal}`}
                            {flight.departureGate &&
                              ` ${t("flights.gate")} ${flight.departureGate}`}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="destination">{flight.destination}</div>
                      <div className="flight-subtext">
                        <span
                          className={
                            flight.arrivalTimeHighlight ? "time-delayed" : ""
                          }
                        >
                          {flight.arrivalTime}
                        </span>
                        {(flight.arrivalGate || flight.arrivalTerminal) && (
                          <span className="gate-info">
                            {flight.arrivalTerminal &&
                              ` • ${t("flights.terminal")} ${flight.arrivalTerminal}`}
                            {flight.arrivalGate &&
                              ` ${t("flights.gate")} ${flight.arrivalGate}`}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="duration-cell">{flight.duration || "—"}</td>
                    <td>
                      <span className={`status ${flight.statusColor}`}>
                        {flight.status}
                      </span>
                      {flight.duration && (
                        <div className="duration-mobile">
                          {t("flights.durationLabel")}: {flight.duration}
                        </div>
                      )}
                      {renderStatusDetail(flight)}
                      <button
                        className="share-flight-btn"
                        onClick={() => handleShareFlight(flight)}
                        aria-label={t("flights.shareFlight")}
                      >
                        <ShareIcon fontSize="inherit" />
                      </button>
                    </td>
                  </tr>
                ))}
                {departures.length === 0 &&
                  arrivals.length === 0 &&
                  !flightsLoading &&
                  searchQuery && (
                    <tr>
                      <td
                        colSpan="6"
                        style={{ textAlign: "center", padding: "20px" }}
                      >
                        {t("flights.noResults")}
                      </td>
                    </tr>
                  )}
              </tbody>
            </table>
          </div>
          {isPreview && (
            <div className="see-all-flights">
              <button
                className="see-all-flights-btn"
                onClick={() => setExpandedTable(name)}
              >
                {t("flights.seeAllFlights")} ›
              </button>
            </div>
          )}
        </div>
      );
    },
    [
      expandedTable,
      flightsLoading,
      searchQuery,
      departures.length,
      arrivals.length,
      t,
      renderStatusDetail,
      handleShareFlight,
      activeTab,
      handleTabChange,
    ],
  );

  return (
    <>
      {expandedTable && (
        <div
          className="tables-modal-overlay"
          onClick={() => setExpandedTable(null)}
        />
      )}
      {selectedHub !== REGIONAL_AIRSPACE.hubCode &&
        (() => {
          const airport = AIRPORTS.find((a) => a.hubCode === selectedHub);
          if (!airport) return null;
          const countryName = t(`countries.${airport.country}`, {
            defaultValue: airport.country,
          });
          return (
            <div className="discover-country-card">
              <div className="discover-country-icon">
                <img
                  src={`/images/flags/${airport.flagCode}.png`}
                  alt={countryName}
                  width="28"
                  height="21"
                  style={{ objectFit: "cover" }}
                />
              </div>
              <div className="discover-country-text">
                <strong>
                  {t("map.discoverCountryTitle", {
                    country: countryName,
                  })}
                </strong>
                <span>
                  {t("map.discoverCountryDesc", {
                    country: countryName,
                  })}
                </span>
              </div>
              <button
                className="discover-country-btn"
                onClick={() => setCountryInfoOpen(true)}
              >
                {t("map.discoverCountryBtn", { country: countryName })}
              </button>
            </div>
          );
        })()}
      {lastUpdated && (
        <div
          className={`last-updated-container ${isRefreshing ? "refreshing" : ""}`}
        >
          <div className="last-updated">
            {isRefreshing
              ? `${t("flights.lastUpdated")}: ${t("flights.refreshing", "Refreshing...")}`
              : `${t("flights.lastUpdated")}: ${lastUpdated.toLocaleTimeString(
                  "en-US",
                  {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                    hour12: false,
                    timeZoneName: "short",
                  },
                )}`}
          </div>
        </div>
      )}

      {searchQuery && onClearSearch && (
        <div className="search-results-banner">
          <div className="search-results-info">
            <span className="search-results-text">
              {t("search.showingResultsFor")} "
              <strong>{searchQuery.toUpperCase()}</strong>"
            </span>
            <span className="search-results-count">
              {t("search.resultsFound", {
                count: departures.length + arrivals.length,
              })}
            </span>
          </div>
          <button
            className="clear-search-button"
            onClick={onClearSearch}
            aria-label={t("search.clearSearch")}
          >
            <Close fontSize="small" />
            <span>{t("search.clearSearch")}</span>
          </button>
        </div>
      )}
      <div
        className={`flight-tables ${expandedTable ? "tables-expanded" : ""}`}
      >
        {renderFlightTable({
          name: "flights",
          headerClass:
            activeTab === "departures"
              ? "departures-header"
              : "arrivals-header",
          icon:
            activeTab === "departures" ? (
              <FlightTakeoffIcon />
            ) : (
              <FlightLandIcon />
            ),
          header: activeTab === "departures" ? departureHeader : arrivalHeader,
          flights: activeTab === "departures" ? departures : arrivals,
          skelPrefix: activeTab === "departures" ? "dep" : "arr",
        })}
      </div>
      <CountryInfo
        isOpen={countryInfoOpen}
        onClose={() => setCountryInfoOpen(false)}
        countryCode={AIRPORTS.find((a) => a.hubCode === selectedHub)?.iataCode}
      />
    </>
  );
}

export default FlightTables;
